import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  collection, 
  getDocs, 
  query,
  orderBy
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import "./styles.css";
import "./auth-management.css";

const ROLES_INFO = {
  viewer: { label: "Viewer", description: "Can view tenders only", color: "#6c757d" },
  engineer: { label: "Engineer", description: "Can edit costing and compliance", color: "#17a2b8" },
  manager: { label: "Manager", description: "Can manage tenders and team", color: "#ffc107" },
  admin: { label: "Admin", description: "Full access to all features", color: "#dc3545" },
};

export default function TeamDirectory() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const q = query(collection(db, "users"), orderBy("displayName", "asc"));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Only show explicitly active users (isActive === true or undefined for backward compatibility)
      setUsers(usersData.filter(u => u.isActive !== false));
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users by search
  const filteredUsers = users.filter((user) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  // Group users by role
  const usersByRole = filteredUsers.reduce((acc, user) => {
    const role = user.role || "viewer";
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading-spinner">Loading team directory...</div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <h1>👥 Team Directory</h1>
        <p>View team members and their roles</p>
      </div>

      <div className="management-toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="user-count">
          {filteredUsers.length} team member{filteredUsers.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div style={{ 
        background: 'white', 
        borderRadius: '8px', 
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Current User Card */}
        <div style={{
          background: '#e3f2fd',
          border: '2px solid #2196f3',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#1976d2' }}>Your Profile</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="user-avatar" style={{ 
              fontSize: '32px', 
              width: '60px', 
              height: '60px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: ROLES_INFO[userProfile?.role || "viewer"]?.color || "#6c757d",
              color: 'white',
              borderRadius: '50%',
              fontWeight: 'bold'
            }}>
              {userProfile?.displayName?.charAt(0).toUpperCase() || "?"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
                {userProfile?.displayName || "User"}
              </div>
              <div style={{ color: '#666', marginBottom: '8px' }}>
                {userProfile?.email}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: ROLES_INFO[userProfile?.role || "viewer"]?.color || "#6c757d",
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  {ROLES_INFO[userProfile?.role || "viewer"]?.label || "Viewer"}
                </span>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  {ROLES_INFO[userProfile?.role || "viewer"]?.description || "Can view tenders only"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Role Groups */}
        {Object.keys(ROLES_INFO).map(roleKey => {
          const roleUsers = usersByRole[roleKey] || [];
          if (roleUsers.length === 0) return null;
          
          const roleInfo = ROLES_INFO[roleKey];
          
          return (
            <div key={roleKey} style={{ marginBottom: '30px' }}>
              <h3 style={{ 
                color: roleInfo.color,
                marginBottom: '15px',
                borderBottom: `2px solid ${roleInfo.color}`,
                paddingBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>
                  {roleInfo.label}s ({roleUsers.length})
                </span>
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                  {roleInfo.description}
                </span>
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '15px'
              }}>
                {roleUsers.map(user => (
                  <div key={user.id} style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: user.id === userProfile?.id ? '#f5f5f5' : 'white'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: roleInfo.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {user.displayName?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '3px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.displayName || "N/A"}
                        {user.id === userProfile?.id && (
                          <span style={{ 
                            marginLeft: '6px', 
                            fontSize: '12px', 
                            color: '#2196f3',
                            fontWeight: 'normal'
                          }}>
                            (You)
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {filteredUsers.length === 0 && (
          <div className="empty-table-message">
            {searchQuery ? "No team members match your search" : "No team members found"}
          </div>
        )}
      </div>
    </div>
  );
}
